import { ReactNode } from 'react'

interface FlowNodeProps {
    title: string
    subtitle: string
    icon?: ReactNode
    x: number
    y: number
    charging?: boolean
}

const FlowNode = ({ title, subtitle, icon, x, y, charging = false }: FlowNodeProps) => {
    return (
        <div
            className={`absolute select-none ${charging ? 'charging-node' : ''}`}
            style={{
                top: `${y}px`,
                left: `${x}px`,
                width: '220px'
            }}
        >
            <style>{`
                @keyframes sparkle {
                    0%, 100% {
                        box-shadow: 0 2px 8px rgba(0,120,212,0.10), 0 0 0px rgba(25, 118, 210, 0);
                    }
                    50% {
                        box-shadow: 0 2px 20px rgba(25, 118, 210, 0.6), 0 0 30px rgba(25, 118, 210, 0.4);
                    }
                }
                .charging-node > div {
                    animation: sparkle 1s ease-in-out infinite;
                }
            `}</style>
            <div style={{
                borderRadius: '10px',
                boxShadow: '0 2px 8px rgba(0,120,212,0.10)',
                background: '#E6F1FB',
                border: '1.5px solid #b3d3f3',
                overflow: 'hidden',
                fontFamily: 'Segoe UI, Arial, sans-serif'
            }}>
                {/* Corner accents */}
                <div style={{ position: 'absolute', top: 6, left: 6, width: 12, height: 12, borderTop: '2px solid #7C3AED', borderLeft: '2px solid #7C3AED', borderTopLeftRadius: 4 }} />
                <div style={{ position: 'absolute', top: 6, right: 6, width: 12, height: 12, borderTop: '2px solid #7C3AED', borderRight: '2px solid #7C3AED', borderTopRightRadius: 4 }} />
                <div style={{ position: 'absolute', bottom: 6, left: 6, width: 12, height: 12, borderBottom: '2px solid #7C3AED', borderLeft: '2px solid #7C3AED', borderBottomLeftRadius: 4 }} />
                <div style={{ position: 'absolute', bottom: 6, right: 6, width: 12, height: 12, borderBottom: '2px solid #7C3AED', borderRight: '2px solid #7C3AED', borderBottomRightRadius: 4 }} />
                {/* Barra de Título */}
                <div style={{
                    background: '#0078D4',
                    color: '#fff',
                    padding: '8px 14px',
                    fontWeight: 600,
                    fontSize: '1.08rem',
                    borderTopLeftRadius: '10px',
                    borderTopRightRadius: '10px',
                    letterSpacing: '0.5px'
                }} className="title-box">
                    {title}
                </div>

                {/* Conteúdo */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '18px 16px 10px 16px',
                    minHeight: '60px'
                }}>
                    {icon && (
                        <div style={{
                            width: '44px',
                            height: '44px',
                            borderRadius: '6px',
                            background: '#fff',
                            boxShadow: '0 1px 4px rgba(0,120,212,0.08)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '24px'
                        }}>
                            {icon}
                        </div>
                    )}
                    <span style={{
                        fontSize: '1.08rem',
                        color: '#1a2a3a',
                        fontWeight: 500
                    }}>
                        {subtitle}
                    </span>
                </div>

                {/* Barra de Ação */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px 0 14px 0',
                    borderTop: '1px solid #d2e6f7',
                    background: '#f7fbfe'
                }}>
                    {/* Ícone de Excluir */}
                    <button
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            outline: 'none',
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '32px',
                            width: '32px'
                        }}
                        title="Excluir"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24">
                            <path fill="#ff6b6b" d="M7 21q-.825 0-1.413-.588Q5 19.825 5 19V7H4V5h5V4h6v1h5v2h-1v12q0 .825-.587 1.412Q17.825 21 17 21Zm10-14H7v12q0 .425.288.712Q7.575 20 8 20h8q.425 0 .713-.288Q17 19.425 17 19Zm-5 8q-.425 0-.712-.288Q11 14.425 11 14V10q0-.425.288-.713Q11.575 9 12 9t.713.287Q13 9.575 13 10v4q0 .425-.287.712Q12.425 15 12 15Zm-4-8v12V7Z"></path>
                        </svg>
                    </button>

                    {/* Ícone de Parâmetros */}
                    <span title="Parâmetros" style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '32px',
                        width: '32px'
                    }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24">
                            <path fill="#6b7a8f" d="M8.7 17.3 2.4 11l6.3-6.3 1.4 1.4L5.2 11l4.9 4.9ZM15.3 17.3l-1.4-1.4L18.8 11l-4.9-4.9 1.4-1.4 6.3 6.3Z"></path>
                        </svg>
                    </span>

                    {/* Ícone de Duplicar */}
                    <button
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            outline: 'none',
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '32px',
                            width: '32px'
                        }}
                        title="Duplicar"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24">
                            <path fill="#3395d7" d="M16 1q.825 0 1.413.587Q18 2.175 18 3v14q0 .825-.587 1.413Q16.825 19 16 19H6q-.825 0-1.413-.587Q4 17.825 4 17V3q0-.825.587-1.413Q5.175 1 6 1Zm0 2H6v14h10Zm4 4v14q0 .825-.587 1.413Q18.825 23 18 23H8v-2h10V7Z"></path>
                        </svg>
                    </button>

                    {/* Ícone de Documentação */}
                    <span title="Documentação" style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '32px',
                        width: '32px'
                    }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24">
                            <path fill="#6b7a8f" d="M6 20q-.825 0-1.413-.587Q4 18.825 4 18V6q0-.825.587-1.413Q5.175 4 6 4h7l5 5v9q0 .825-.587 1.413Q18.825 20 18 20Zm7-11V6H6v12h12V9Zm-5 7h4v-2H8Zm0-3h8v-2H8Z"></path>
                        </svg>
                    </span>
                </div>
            </div>
        </div>
    )
}

export default FlowNode
